import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Rocket, Trash2, Presentation, FileText } from "lucide-react";

/**
 * 3 dialogs administrativos que ficavam no fim do Dashboard.jsx:
 *  - PrepareLaunchDialog: limpa dados de teste antes do go-live
 *  - ModoApresentacaoDialog: zera TODAS as empresas para demo ao cliente
 *  - PurgeEmpresaDialog: reatribui ou apaga tudo de uma empresa
 * Todo o estado + callbacks vem via props (state permanece em Dashboard.jsx).
 */
export function PrepareLaunchDialog({ launchEmpresa, setLaunchEmpresa, onConfirm }) {
  return (
    <Dialog open={!!launchEmpresa} onOpenChange={(o) => !o && setLaunchEmpresa(null)}>
      <DialogContent data-testid="dialog-prepare-launch">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <Rocket className="w-5 h-5" />Preparar "{launchEmpresa?.nome}" para lançamento
          </DialogTitle>
        </DialogHeader>
        {launchEmpresa && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">Ao preparar para lançamento oficial, o sistema vai:</div>
            <div className="border-2 border-rose-300 bg-rose-50 rounded p-3 text-sm">
              <div className="font-bold text-rose-900 mb-1">🗑️ Apagar dados de teste</div>
              <ul className="list-disc list-inside text-rose-800 space-y-0.5">
                <li><b>{launchEmpresa.count}</b> entrega(s) removidas</li>
                <li>Todos os motoristas cadastrados removidos</li>
                <li>Mensagens de chat das entregas removidas</li>
                <li>Usuários da empresa removidos <span className="text-xs">(exceto Super Admin)</span></li>
                <li>Todos os logs de auditoria zerados</li>
              </ul>
            </div>
            <div className="border-2 border-emerald-300 bg-emerald-50 rounded p-3 text-sm">
              <div className="font-bold text-emerald-900 mb-1">✅ PRESERVADO</div>
              <ul className="list-disc list-inside text-emerald-800 space-y-0.5">
                <li>Bairros, regiões, tipos de entrega, formas de pagamento</li>
                <li>Super Admin (você) e login de apresentação</li>
                <li>Configurações da empresa (logo, CNPJ, template WhatsApp, flags)</li>
              </ul>
            </div>
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
              <b>Não pode ser desfeito.</b> Use antes do "go-live" para eliminar dados de teste sem perder a configuração já feita.
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={() => setLaunchEmpresa(null)}>Cancelar</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={onConfirm} data-testid="btn-confirm-launch">
                <Rocket className="w-4 h-4 mr-2" />Preparar para lançamento
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ModoApresentacaoDialog({
  open, onClose, empresas, demoResult, demoLoading,
  onExecutar, onBaixarPdf,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dialog-modo-apresentacao">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-700">
            <Presentation className="w-5 h-5" />Modo Apresentação
          </DialogTitle>
        </DialogHeader>
        {!demoResult ? (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Ativa o ambiente para uma demonstração 100% limpa ao cliente. Uma única ação zera TODAS as empresas.
            </div>
            <div className="border-2 border-rose-300 bg-rose-50 rounded p-3 text-sm">
              <div className="font-bold text-rose-900 mb-1">🗑️ Será APAGADO (em todas as {empresas.length} empresas)</div>
              <ul className="list-disc list-inside text-rose-800 space-y-0.5">
                <li>Todas as entregas + mensagens de chat</li>
                <li>Todos os entregadores cadastrados</li>
                <li>Todos os usuários exceto Super Admin(s)</li>
                <li>Todos os logs de auditoria</li>
              </ul>
            </div>
            <div className="border-2 border-emerald-300 bg-emerald-50 rounded p-3 text-sm">
              <div className="font-bold text-emerald-900 mb-1">✅ PRESERVADO</div>
              <ul className="list-disc list-inside text-emerald-800 space-y-0.5">
                <li>Empresas cadastradas e suas configurações (logo, CNPJ, templates)</li>
                <li>Bairros, regiões, tipos de entrega e formas de pagamento (por empresa)</li>
                <li>Super Admin(s) — você continua logado</li>
              </ul>
            </div>
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
              <b>Não pode ser desfeito.</b> Após a ação, você poderá baixar um PDF com o estado inicial da demo para apresentar ao cliente com transparência total.
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={onClose} disabled={demoLoading}>Cancelar</Button>
              <Button className="bg-purple-600 hover:bg-purple-700" disabled={demoLoading} onClick={onExecutar} data-testid="btn-confirm-modo-apresentacao">
                <Presentation className="w-4 h-4 mr-2" />{demoLoading ? "Processando..." : "Ativar Modo Apresentação"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="modo-apresentacao-resultado">
            <div className="border-2 border-emerald-300 bg-emerald-50 rounded p-3 text-sm">
              <div className="font-bold text-emerald-900 mb-1">✅ Ambiente pronto para apresentação</div>
              <div className="grid grid-cols-2 gap-2 text-emerald-800 text-xs">
                <div><b>{demoResult.resumo.empresas_processadas}</b> empresa(s) zeradas</div>
                <div><b>{demoResult.resumo.super_admins}</b> Super Admin(s) ativos</div>
                <div><b>{demoResult.resumo.entregas_removidas}</b> entregas removidas</div>
                <div><b>{demoResult.resumo.entregadores_removidos}</b> entregadores removidos</div>
                <div><b>{demoResult.resumo.usuarios_removidos}</b> usuários removidos</div>
                <div><b>{demoResult.resumo.audit_logs_removidos}</b> logs de auditoria zerados</div>
              </div>
            </div>
            <div className="border border-slate-200 rounded p-3 text-xs">
              <div className="font-semibold mb-1">Cadastros preservados por empresa:</div>
              <div className="max-h-40 overflow-auto">
                {demoResult.empresas.map((e) => (
                  <div key={e.empresa_id} className="flex justify-between py-1 border-b last:border-0">
                    <span className="font-medium">{e.empresa}</span>
                    <span className="text-muted-foreground">
                      {e.config_preservada.bairros} bairros · {e.config_preservada.regioes} regiões · {e.config_preservada.tipos} tipos · {e.config_preservada.formas_pgto} formas pgto
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={onClose}>Fechar</Button>
              <Button className="bg-sky-600 hover:bg-sky-700" onClick={onBaixarPdf} data-testid="btn-baixar-pdf-apos-reset">
                <FileText className="w-4 h-4 mr-2" />Baixar PDF do estado inicial
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PurgeEmpresaDialog({ purgeEmpresa, setPurgeEmpresa, onReatribuir, onPurge }) {
  return (
    <Dialog open={!!purgeEmpresa} onOpenChange={(o) => !o && setPurgeEmpresa(null)}>
      <DialogContent data-testid="dialog-excluir-empresa">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <Trash2 className="w-5 h-5" />Excluir empresa "{purgeEmpresa?.nome}"
          </DialogTitle>
        </DialogHeader>
        {purgeEmpresa && (
          <div className="space-y-3">
            <div className="text-sm">Esta empresa tem <b>{purgeEmpresa.count}</b> entrega(s). Escolha o que fazer:</div>
            <div className="border border-amber-200 bg-amber-50 rounded p-3 space-y-1 text-sm">
              <div className="font-bold text-amber-900">Reatribuir (recomendado)</div>
              <div className="text-amber-800">Move as entregas para outra empresa existente. Preserva histórico e auditoria.</div>
            </div>
            <div className="border-2 border-rose-300 bg-rose-50 rounded p-3 space-y-1 text-sm">
              <div className="font-bold text-rose-900">Apagar tudo (limpar tenant)</div>
              <div className="text-rose-800">Remove permanentemente <b>{purgeEmpresa.count} entrega(s)</b> + mensagens de chat desta empresa. Use ao ceder o app para um novo cliente. <b>Não pode ser desfeito.</b></div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={() => setPurgeEmpresa(null)}>Cancelar</Button>
              <Button variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-50" onClick={onReatribuir} data-testid="btn-empresa-reatribuir">
                Reatribuir entregas
              </Button>
              <Button className="bg-rose-600 hover:bg-rose-700" onClick={onPurge} data-testid="btn-empresa-purge">
                Apagar tudo
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
